import React, { useEffect, useState } from "react";
import { graphql, navigate } from "gatsby";
import PropTypes from "prop-types";
import cx from "classnames";
import { Header, getLangFromLocale } from "@sunwing/wvi-components";
import { usePageContext } from "../../context/PageContext";
import { useNotifications } from "../../context";
import { LinkConnector } from "../LinkConnector";
import { linkMapper } from "../../utils/links";
import { RCL as useTranslation } from "../../components/RCL";
import { ComponentBlockConnector } from "../ComponentBlockConnector";
import { languageToggleAdapter } from "../../components/LanguageToggle";
import { LanguageToggleConnector } from "../LanguageToggleConnector";
import { LinkButtonConnector } from "../LinkButtonConnector";
import { NotificationsConnector } from "../NotificationsConnector";
import * as styles from "./HeaderConnector.module.scss";

const HeaderConnector = ({
	data,
	pathname,
	altLangPages,
	pageType = "Packages",
	headerNavigation,
}) => {
	const pageHeader = headerNavigation || data.defaultHeader || {};

	const { locale, setPreferredGateway, preferredGateway } = usePageContext();
	const { notifications: notificationsLiveData } = useNotifications();

	const [currentGateway, setCurrentGateway] = useState({
		label: "Toronto",
		value: "YYZ",
	});
	const lang = getLangFromLocale(locale?.toLowerCase());

	const labels = {
		header: {
			backToHome: useTranslation({ searchKey: "back-to-home-wvi" }),
			travelAdvisories: useTranslation({ searchKey: "travel-advisories" }),
			lang: useTranslation({ searchKey: "languagePreference" }),
			menu: useTranslation({ searchKey: "menu" }),
			menuTitle: useTranslation({ searchKey: "menu" }),
			openMenu: useTranslation({ searchKey: "a11y-open-menu" }),
			closeMenu: useTranslation({ searchKey: "a11y-close-menu" }),
			openGatewayMenu: useTranslation({ searchKey: "a11y-open-menu" }),
			changeGateway: useTranslation({ searchKey: "change-gateway" }),
			gatewayHeader: useTranslation({ searchKey: "gateway-header" }),
			closeModal: useTranslation({ searchKey: "modal-close" }),
			searchLabel: useTranslation({ searchKey: "search" }),
			searchHotelsLabel: useTranslation({ searchKey: "search-hotels" }),
		},
		logo: {
			desktop: useTranslation({ searchKey: "wvi-logo-title" }),
			mobile: useTranslation({ searchKey: "wvi-logo-title" }),
		},
	};

	const getHomepageUrl = () => {
		const homepageUrl = data.homepagePaths?.edges?.find(edge => {
			const homepageLocale = edge?.node?.context?.locale?.toUpperCase()?.substring(0, 2);
			return homepageLocale === lang.toUpperCase();
		});

		return homepageUrl?.node?.path;
	};

	const homePageUrl = getHomepageUrl();
	let logoLinkPageUrl;

	if (pageHeader.logoLink?.slug) {
		logoLinkPageUrl =
			pageHeader.logoLink.slug !== "/" ? homePageUrl + pageHeader.logoLink.slug : homePageUrl;
	}

	const handleChangeGateway = gateway => {
		setPreferredGateway(gateway);
		setCurrentGateway(gateway);
	};

	const notificationStaticData = data?.notifications?.edges ?? [];
	const backToHome =
		pageHeader.logoLink?.slug !== "/" && pageHeader.logoLink?.slug
			? {
					url: homePageUrl,
					label: labels.header.backToHome,
				}
			: undefined;

	useEffect(() => {
		if (preferredGateway) {
			setCurrentGateway(preferredGateway);
		}
	}, [preferredGateway]);

	return (
		<>
			<Header
				locale={locale}
				gatewayConfig={{
					brand: process.env.GATSBY_HOTEL_INFOSERVICE_BRAND || "wvi",
					url: process.env.GATSBY_HOTEL_INFOSERVICE_API,
					options: {
						headers: {
							"Ocp-Apim-Subscription-Key": process.env.GATSBY_HOTEL_INFOSERVICE_APIM_SUB_KEY,
						},
					},
				}}
				classNames={{
					headerTopWrapper: styles.headerTopWrapper,
					headerWrapper: cx(styles.headerWrapper),
					header: styles.headerContainer,
					logo: styles.logoDesktop,
					menuContainer: styles.menuContainer,
					backToHomeContainer: styles.backToHomeContainer,
				}}
				handleHotelSearch={hotelUrl => {
					navigate(hotelUrl);
				}}
				backToHome={backToHome}
				logoOnly={pageHeader.displayLogoOnly}
				labels={labels.header}
				logo={{
					desktop: {
						src:
							pageHeader.logo?.[0]?.format === "svg"
								? pageHeader.logo?.[0]?.original_secure_url?.replace(
										"/image/upload/",
										"/image/upload/f_svg/"
									)
								: pageHeader.logo?.[0]?.secure_url,
						width: "171px",
						alt: labels.logo.desktop,
					},
					mobile: {
						width: "36px",
						alt: labels.logo.mobile,
						src:
							pageHeader.mobileLogo?.[0]?.format === "svg"
								? pageHeader.mobileLogo?.[0]?.original_secure_url?.replace(
										"/image/upload/",
										"/image/upload/f_svg/"
									)
								: pageHeader.mobileLogo?.[0]?.secure_url,
					},
				}}
				logoLink={logoLinkPageUrl}
				currentGateway={currentGateway}
				handleChangeGateway={handleChangeGateway}
				altLangPages={languageToggleAdapter(altLangPages)}
				links={linkMapper(pageHeader.links)}
				menuLinks={linkMapper(pageHeader.menu?.[0]?.menuItems)}
				renderLinks={link => (
					<LinkConnector
						to={link.href || link.url}
						target={link.target}
						className={link.className}
						noStyle
						showExternalIcon={false}>
						{link.children}
					</LinkConnector>
				)}
				renderMenuLink={(link, { className, activeClassName, unmountTrap }) => (
					<LinkButtonConnector
						data={link}
						activeClassName={activeClassName}
						partiallyActive={link.url === pathname}
						className={className}
						onLinkModalOpen={event =>
							unmountTrap(event, link?.modal && link?.target === "EXTERNAL_MODAL")
						}
					/>
				)}
				renderLanguageToggle={(_altLangPages, propsLT) => (
					<LanguageToggleConnector
						altLangPages={_altLangPages}
						classNames={propsLT.classNames}
						variant="drop-down"
						locale={propsLT.locale}
					/>
				)}
				renderLinkLogo={(link, logo) => (
					<LinkConnector
						to={link.href}
						target={link.target}
						className={link.className}
						showExternalIcon={false}
						noStyle>
						<img
							src={logo.src}
							alt={logo.alt}
							width={logo.width}
							height={logo.height}
							className={logo.className}
						/>
					</LinkConnector>
				)}
				checkActiveLink={() => false}>
				{!pageHeader.displayLogoOnly && (notificationStaticData || notificationsLiveData) && (
					<div className={styles.notificationsContainer} slot="notifications">
						<NotificationsConnector
							notificationsLiveData={notificationsLiveData}
							notificationsStaticData={notificationStaticData}
							locale={locale}
						/>
					</div>
				)}
				{/* BOOKING SEARCH WIDGET */}
				{pageType !== "Other" && pageType !== "Profile" && (
					<ComponentBlockConnector
						slot="widget"
						data={{
							lang,
							type: pageType?.toLowerCase(),
							...pageHeader.widget,
						}}
					/>
				)}
			</Header>
		</>
	);
};

HeaderConnector.propTypes = {
	data: PropTypes.shape({
		defaultHeader: PropTypes.object.isRequired,
		homepagePaths: PropTypes.shape({
			edges: PropTypes.arrayOf(
				PropTypes.shape({
					node: PropTypes.shape({
						context: PropTypes.shape({
							locale: PropTypes.string,
						}),
						path: PropTypes.string,
					}),
				})
			),
		}).isRequired,
		notifications: PropTypes.shape({
			edges: PropTypes.array,
		}),
	}).isRequired,
	pathname: PropTypes.string.isRequired,
	altLangPages: PropTypes.shape({
		edges: PropTypes.arrayOf(
			PropTypes.shape({
				node: PropTypes.shape({
					context: PropTypes.shape({
						id: PropTypes.string,
						locale: PropTypes.string,
					}),
					path: PropTypes.string,
				}),
			})
		),
	}).isRequired,
	headerNavigation: PropTypes.object,
	pageType: PropTypes.oneOf([
		"LastMinute",
		"Packages",
		"Flights",
		"Hotels",
		"Cruises",
		"Other",
		"Profile",
	]),
};

export const query = graphql`
	query GetDefaultLocalizedHeader($locale: String!) {
		defaultHeader: contentfulHeaderNavigation(
			contentful_id: { eq: "56yIKqXcdal7UqIypah9NF" }
			node_locale: { eq: $locale }
		) {
			...HeaderNavigation
		}
		homepagePaths: allSitePage(filter: { context: { template: { eq: "homepage" } } }) {
			edges {
				node {
					path
					context {
						locale
					}
				}
			}
		}
		notifications: allContentfulNotifications(filter: { node_locale: { eq: $locale } }) {
			edges {
				node {
					heading
					contentful_id
					message {
						raw
					}
					entry {
						... on ContentfulHotelChain {
							slug
							sys {
								contentType {
									sys {
										id
									}
								}
							}
						}
						... on ContentfulDestination {
							slug
							sys {
								contentType {
									sys {
										id
									}
								}
							}
						}
						... on ContentfulPage {
							slug
							sys {
								contentType {
									sys {
										id
									}
								}
							}
						}
						... on ContentfulTravelAdvisory {
							displayTitle
							content {
								raw
							}
							contentful_id
							sys {
								contentType {
									sys {
										id
									}
								}
							}
							activeDate(fromNow: false)
						}
					}
					subheading
					updatedAt(fromNow: false)
				}
			}
		}
	}
`;

export default HeaderConnector;
export { HeaderConnector };
